import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

const WorkflowTrackDialog = ({
  isOpen,
  onClose,
  initialTrackId = '',
  initialTrackLink = '',
  messageId,
  onSave,
  zIndexClass = 'z-[1100]',
}) => {
  const [trackId, setTrackId] = useState('');
  const [trackLink, setTrackLink] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTrackId(initialTrackId || '');
      setTrackLink(initialTrackLink || '');
    } else {
      setTrackId('');
      setTrackLink('');
    }
  }, [isOpen, initialTrackId, initialTrackLink]);

  const handleClose = () => {
    setTrackId('');
    setTrackLink('');
    onClose();
  };

  const handleSave = () => {
    const trimmedId = trackId.trim();
    const trimmedLink = trackLink.trim();
    if (!trimmedId || !trimmedLink) return;
    if (onSave) {
      onSave({
        trackId: trimmedId,
        trackLink: trimmedLink,
        messageId,
      });
    }
    handleClose();
  };

  const handleRemove = () => {
    if (onSave) {
      onSave({
        trackId: '',
        trackLink: '',
        messageId,
        removed: true,
      });
    }
    handleClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={`fixed inset-0 ${zIndexClass} overflow-y-auto`}>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full transform transition-all">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {initialTrackId ? 'Edit Track ID' : 'Add Track ID'}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label htmlFor="workflow-track-id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Track ID
              </label>
              <input
                type="text"
                id="workflow-track-id"
                value={trackId}
                onChange={(e) => setTrackId(e.target.value)}
                placeholder="e.g. PROJ-123"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
            <div>
              <label htmlFor="workflow-track-link" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Link
              </label>
              <input
                type="url"
                id="workflow-track-link"
                value={trackLink}
                onChange={(e) => setTrackLink(e.target.value)}
                placeholder="https://jira.example.com/browse/PROJ-123"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            {initialTrackId ? (
              <button
                type="button"
                onClick={handleRemove}
                className="px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40 rounded-lg transition-colors"
              >
                Remove
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!trackId.trim() || !trackLink.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WorkflowTrackDialog;
